/**
 * Utility functions for installer API calls
 */

/**
 * Get CSRF token from meta tag
 * @returns {string} CSRF token
 * @throws {Error} If CSRF token is not found
 */
export function getCsrfToken() {
  const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
  if (!token) {
    console.error('CSRF token not found. This may cause installation issues.')
    // Return a fallback token for installer (will be validated server-side)
    return 'installer-csrf-fallback'
  }
  return token
}

/**
 * Update the CSRF meta tag after a successful installer request.
 * @param {string|null|undefined} token
 */
export function updateCsrfToken(token) {
  if (!token) {
    return
  }

  const meta = document.querySelector('meta[name="csrf-token"]')
  if (meta) {
    meta.setAttribute('content', token)
  }
}

/**
 * Make an authenticated API request to installer endpoints
 * @param {string} url - The API endpoint URL
 * @param {Object} options - Fetch options
 * @returns {Promise<Response>} Fetch response
 */
export async function installerApiRequest(url, options = {}) {
  const csrfToken = getCsrfToken()

  const defaultOptions = {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': csrfToken,
      'Accept': 'application/json',
      'X-Requested-With': 'XMLHttpRequest',
    },
    credentials: 'same-origin',
  }

  const mergedOptions = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  }

  return fetch(url, mergedOptions)
}

/**
 * Handle API response and extract JSON data
 * @param {Response} response - Fetch response
 * @returns {Promise<Object>} Parsed JSON data
 * @throws {Error} If response is not ok or JSON parsing fails
 */
export async function handleApiResponse(response) {
  const rawBody = await response.text()
  let data = null

  if (rawBody) {
    try {
      data = JSON.parse(rawBody)
    } catch (e) {
      const preview = rawBody.trim().slice(0, 240).replace(/\s+/g, ' ')
      const hint = preview.startsWith('<')
        ? 'The server returned HTML instead of JSON.'
        : 'The server returned a non-JSON response.'

      if (preview.toLowerCase().includes('deprecated:')) {
        throw new Error(
          `${hint} PHP deprecation output may have broken the installer response. Use PHP 8.3 for this app, or retry after clearing bootstrap/cache/config.php.`
        )
      }

      throw new Error(
        preview
          ? `${hint} ${preview}`
          : 'The server returned an empty response. If you are using `php artisan serve`, retry the installation — the app may have restarted while writing .env.'
      )
    }
  }

  if (!response.ok) {
    throw new Error(data?.message || response.statusText || 'Request failed')
  }

  if (!data) {
    throw new Error(
      'The server returned an empty response. The install request may have timed out or the dev server may have restarted during migrations.'
    )
  }

  updateCsrfToken(data.csrfToken)

  return data
}
